import type { AgentConfig, AgentToolOptions, UserAgentConfig } from '@/types/agents'
import type { ModelConfig } from '@/types/models'
import type { ProviderConfig } from '@/types/providers'
import type { Assistant } from '@/types/assistants'

// Lens adapter: maps real AgentConfig records to the plain-language "Assistant" view and back.
// An Assistant is NOT a new record type — it is a view over an agent. The "brain" is just the agent's
// configured model, shown by its own name. See DESIGN_PHILOSOPHY.md §4.
//
// IMPORTANT: AgentConfig.model holds the ModelConfig *id* (the Advanced editor saves the model's id;
// see AgentCard.vue). Older/seeded records may hold the name instead, so every lookup resolves by id
// OR name — mirroring AgentCard.getModelConfig — otherwise nothing matches and no brain is selected.

/** Resolve an agent's model reference (a ModelConfig id or name) to its ModelConfig, or null. */
export function resolveModelConfig(key: string, userModels: ModelConfig[]): ModelConfig | null {
  if (!key) return null
  return userModels.find((m) => m.id === key) ?? userModels.find((m) => m.name === key) ?? null
}

/** Friendly display name for a model: its configured name, else its raw provider model id. */
export function brainLabelForModel(model: ModelConfig | null): string {
  if (!model) return ''
  return model.name || model.model
}

// userTools is meant to be a list of server-name strings, but older/config-agent/backend-created
// records can hold object entries (e.g. `[{}]`). Those render as an "[object Object]" chip in the
// editor's tools select — coerce to clean strings here so the lens only ever exposes real names
// (objects contribute their `name`/`serverName` if present, else are dropped). Also self-heals: the
// cleaned list is what gets written back on the next save.
function normalizeUserTools(userTools: unknown): string[] {
  if (!Array.isArray(userTools)) return []
  return userTools
    .map((t) => {
      if (typeof t === 'string') return t
      if (t && typeof t === 'object') {
        const o = t as { name?: unknown; serverName?: unknown }
        if (typeof o.name === 'string') return o.name
        if (typeof o.serverName === 'string') return o.serverName
      }
      return ''
    })
    .filter((t): t is string => t !== '')
}

/** Read direction: one AgentConfig → its Assistant lens. */
export function assistantFromAgent(c: AgentConfig): Assistant {
  const published = !!c.publishedInfo?.isPublished
  return {
    id: c.id,
    name: c.name,
    published,
    status: published ? 'live' : 'draft',
    desc: c.description || '',
    instructions: c.systemPrompt || '',
    voice: c.voice?.savedVoiceName || null,
    tools: normalizeUserTools(c.toolOptions?.userTools),
    memory: !!(c.embeddingOptions && c.embeddingOptions.collections?.length)
  }
}

/** Compose the assistant list from the agents store. Read-only lens. */
export function assistantsFromStores(args: { agents: AgentConfig[] }): Assistant[] {
  return (args.agents || []).map((a) => assistantFromAgent(a))
}

// `selectedFunctions` is a per-server NARROWING override, handled exactly as the Advanced AgentCard
// does it: the engine exposes ALL of a server's functions when that server is absent from this map
// (missionsquad-core `filterToolsWithSelection`), and an empty array means "none". Two rules matter:
//   1. Always return an object. A null/undefined map is dropped by the save API, which makes the
//      backend treat `userTools` (server names) as function names and wipe the tools entirely.
//   2. Prune entries for servers the user just de-selected. A newly enabled tool is then simply
//      absent → all its functions are on (the non-technical default), while any per-function
//      narrowing previously set in Advanced for a still-enabled server is preserved untouched.
function pruneSelectedFunctions(
  selectedFunctions: AgentToolOptions['selectedFunctions'],
  enabledServers: string[]
): NonNullable<AgentToolOptions['selectedFunctions']> {
  const result: { [serverName: string]: string[] } = {}
  if (!selectedFunctions) return result
  const enabled = new Set(enabledServers)
  for (const [server, fns] of Object.entries(selectedFunctions)) {
    if (enabled.has(server) && fns) result[server] = fns
  }
  return result
}

// Shared AgentToolOptions constructor, used by both the Advanced AgentCard save path and the
// simple-mode applyAssistantToAgent below. It owns rule 1 above — selectedFunctions is ALWAYS an
// object — so neither surface can regress into the tools-wiping null map. The two surfaces
// intentionally differ on rule 2: the simple editor prunes narrowing for de-selected servers
// (pruneDeselected: true); Advanced keeps every entry so re-enabling a server restores its
// previous per-function narrowing.
export function buildAgentToolOptions(args: {
  systemTools: string[]
  userTools: string[]
  selectedFunctions: AgentToolOptions['selectedFunctions']
  pruneDeselected?: boolean
}): AgentToolOptions {
  return {
    systemTools: args.systemTools,
    userTools: args.userTools,
    selectedFunctions: args.pruneDeselected
      ? pruneSelectedFunctions(args.selectedFunctions, args.userTools)
      : args.selectedFunctions || {}
  }
}

// Write direction: fold an edited Assistant back onto its real AgentConfig. Returns a NEW config to
// dispatch through the existing `setAgentConfig` action — it never mutates the input. The model is
// selected directly by id in the editor (BrainPicker is id-based), so it is passed in here rather
// than derived from the brain tier. A null modelId leaves the agent's model untouched.
export function applyAssistantToAgent(
  assistant: Assistant,
  agent: AgentConfig,
  modelId: string | null,
  selectedFunctions?: AgentToolOptions['selectedFunctions']
): AgentConfig {
  const next: AgentConfig = { ...agent }
  next.name = assistant.name
  next.description = assistant.desc
  next.systemPrompt = assistant.instructions

  if (modelId) next.model = modelId

  next.voice = assistant.voice ? { ...(agent.voice ?? {}), savedVoiceName: assistant.voice } : undefined

  next.toolOptions = buildAgentToolOptions({
    systemTools: agent.toolOptions?.systemTools ?? [],
    userTools: assistant.tools,
    // Per-function ("specific actions") narrowing the simple editor now manages via the reused
    // FunctionSelectionDialog. Falls back to the source agent's map when the caller doesn't pass one,
    // so old callers keep passing it through losslessly. pruneDeselected drops entries for servers the
    // user just de-selected (rule 2 in buildAgentToolOptions).
    selectedFunctions: selectedFunctions ?? agent.toolOptions?.selectedFunctions,
    pruneDeselected: true
  })

  return next
}

/** Provider keys with a usable connection (built-ins need a key; custom providers may use a url). */
export function connectedProviderSet(providers: ProviderConfig[]): Set<string> {
  return new Set((providers || []).filter((p) => p.apiKey || p.url).map((p) => p.providerKey))
}

export type AssistantSortOrder = 'default' | 'asc' | 'desc'

// Mirror of the Advanced Agents.vue filter/sort, extracted so the simple Assistants lens reuses the
// same behavior instead of re-implementing it: filter by name OR description, then optionally sort by
// name. Filtering is case-insensitive (the Advanced inline copy compares case-sensitively). Returns a
// NEW array; never mutates the input.
export function filterSortAssistants(
  list: UserAgentConfig[],
  search: string | null,
  sortOrder: AssistantSortOrder
): UserAgentConfig[] {
  const term = (search ?? '').trim().toLowerCase()
  const filtered = term
    ? (list ?? []).filter(
        (a) =>
          a.config.name.toLowerCase().includes(term) ||
          (a.config.description?.toLowerCase().includes(term) ?? false)
      )
    : [...(list ?? [])]

  if (sortOrder === 'default') return filtered

  return filtered.sort((a, b) => {
    const cmp = a.config.name.toLowerCase().localeCompare(b.config.name.toLowerCase())
    return sortOrder === 'asc' ? cmp : -cmp
  })
}
